import { useEffect, useState } from 'react'
import { UserPlus, UserCheck, Zap, ArrowLeft, CheckCircle2, ShieldCheck, Smartphone, Bell } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import api from '../api/client'
import SearchableSelect from '../components/SearchableSelect'
import CamposRenderer from '../components/CamposRenderer'
import ConexaoGD from '../components/ConexaoGD'
import { successModal, errorModal } from '../components/ui'

function load(key) { try { return JSON.parse(localStorage.getItem(key) || 'null') } catch { return null } }

export default function NovaConexao() {
  const nav = useNavigate()
  const isAdmin = (load('user')?.perfil || '') === 'admin'
  const meuLic = load('licenciado')

  const [step, setStep] = useState(1)            // 1=cliente novo/existente, 2=serviço, 3=form, 4=aceite
  const [modo, setModo] = useState(null)         // 'novo' | 'existente'
  const [licenciados, setLic] = useState([])
  const [clientes, setCli] = useState([])
  const [ofertas, setOf] = useState([])

  const [licId, setLicId] = useState(meuLic?.id || '')
  const [clienteId, setClienteId] = useState('')
  const [indicadorId, setIndicadorId] = useState('')
  const [novoCli, setNovoCli] = useState({ nome: '', cpf_cnpj: '', email: '', whatsapp: '' })
  const [ofertaId, setOfertaId] = useState('')
  const [dados, setDados] = useState({})
  const [arquivos, setArquivos] = useState({})
  const [criado, setCriado] = useState(null)
  const [msg, setMsg] = useState('')

  useEffect(() => {
    if (isAdmin) api.get('/licenciados?limit=100').then((r) => setLic(r.data.data || [])).catch(() => {})
    api.get('/clientes?limit=100').then((r) => setCli(r.data.data || [])).catch(() => {})
    // PDV de serviços recorrentes: SOMENTE serviços (produtos físicos vão em "Fazer Pedido")
    // Proteção Veicular tem card de categoria próprio (fluxo de cotação) — não listar como serviço comum
    api.get('/ofertas/publico?tipo=servico').then((r) => setOf((r.data || []).filter((o) => o.recorrente && o.categoria !== 'protecao_veicular'))).catch(() => {})
  }, [isAdmin])

  const ofertaSel = ofertas.find((o) => o.id === Number(ofertaId))
  // Detecta por CATEGORIA (campo controlado) — evita falso-positivo por nome de produto contendo "alarme"
  const isAlarme = (o) => (o.categoria || '').toLowerCase().includes('alarme')
  const energiaOferta = ofertas.find((o) => o.categoria === 'energia')
  const alarmeOferta = ofertas.find(isAlarme)
  const recorrentesOutros = ofertas.filter((o) => o.categoria !== 'energia' && !isAlarme(o))

  function abrirEnergia() {
    if (!energiaOferta) { errorModal('Nenhum serviço de Energia por Assinatura configurado pelo administrador.'); return }
    setOfertaId(energiaOferta.id); irParaForm()
  }
  function abrirAlarme() {
    if (!alarmeOferta) { successModal('Sistema de Alarme Smart estará disponível em breve.', { title: 'Em breve' }); return }
    setOfertaId(alarmeOferta.id); irParaForm()
  }

  function escolherModo(m) { setModo(m); setStep(2) }

  async function irParaForm() {
    setMsg('')
    setDados({}); setArquivos({})
    setStep(3)
  }

  async function criar() {
    setMsg('')
    let cli_id = clienteId
    try {
      // Novo cliente: cria primeiro (vínculo obrigatório ao licenciado)
      if (modo === 'novo') {
        if (!novoCli.nome || !novoCli.email) { setMsg('Informe pelo menos nome e e-mail do cliente.'); return }
        const lid = isAdmin ? Number(licId) : undefined
        if (isAdmin && !lid) { setMsg('Selecione o licenciado responsável.'); return }
        const { data: c } = await api.post('/clientes', { ...novoCli, licenciado_id: lid, oferta_id: Number(ofertaId) })
        cli_id = c.id
      }
      if (!cli_id) { setMsg('Selecione o cliente.'); return }
      const licFinal = isAdmin ? Number(licId) : (meuLic?.id)
      if (!licFinal) { setMsg('Licenciado responsável não identificado.'); return }
      // valida campos obrigatórios da oferta
      for (const c of (ofertaSel?.campos_schema || [])) {
        if (!c.obrigatorio || c.tipo === 'secao') continue
        if (c.tipo === 'arquivo') { if (!arquivos[c.chave]) { setMsg(`Envie o arquivo: ${c.label}`); return } }
        else if (dados[c.chave] === undefined || dados[c.chave] === '' || dados[c.chave] === null) { setMsg(`Preencha: ${c.label}`); return }
      }
      const body = {
        licenciado_id: licFinal, cliente_id: Number(cli_id), tipo_conexao: 'servico',
        oferta_id: Number(ofertaId), pv: ofertaSel?.pv || 0, dados_customizados: dados,
        cliente_indicador_id: indicadorId ? Number(indicadorId) : null,
      }
      const { data } = await api.post('/contratos-adesao', body)
      for (const [chave, file] of Object.entries(arquivos)) {
        if (!file) continue
        const fd = new FormData(); fd.append('tipo', chave); fd.append('file', file)
        try { await api.post(`/contratos-adesao/${data.id}/documentos`, fd) } catch { /* segue */ }
      }
      setCriado(data); setStep(4)
      successModal('Cadastro enviado para a Fila de Análise. Você será notificado quando o administrador concluir a análise.', { title: 'Enviado para análise!' })
    } catch (e) { errorModal(e?.response?.data?.detail || 'Erro ao criar conexão') }
  }

  function reset() {
    setStep(1); setModo(null); setClienteId(''); setIndicadorId(''); setNovoCli({ nome: '', cpf_cnpj: '', email: '', whatsapp: '' })
    setOfertaId(''); setDados({}); setArquivos({}); setCriado(null); setMsg('')
  }

  const ICONS = { /* ícone padrão raio p/ serviços */ }
  const inp = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-accent-400'

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-2xl font-heading font-bold text-gray-900">Nova Conexão</h1>
        {step > 1 && !criado && (
          <button onClick={() => setStep(step - 1)} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800"><ArrowLeft size={15} /> Voltar</button>
        )}
      </div>

      {/* PASSO 1 — Novo / Existente */}
      {step === 1 && (
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
          <p className="text-center text-gray-500 mb-5">Como deseja cadastrar?</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <button onClick={() => escolherModo('novo')} className="text-left rounded-2xl border border-gray-200 p-5 hover:border-accent-400 hover:shadow-md transition">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow mb-3"><UserPlus size={20} className="text-white" /></div>
              <p className="font-heading font-bold text-gray-900">Novo Cliente</p>
              <p className="text-xs text-gray-500 mt-1">Cadastrar cliente novo no sistema com o serviço</p>
            </button>
            <button onClick={() => escolherModo('existente')} className="text-left rounded-2xl border border-gray-200 p-5 hover:border-accent-400 hover:shadow-md transition">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center shadow mb-3"><UserCheck size={20} className="text-white" /></div>
              <p className="font-heading font-bold text-gray-900">Cliente Cadastrado</p>
              <p className="text-xs text-gray-500 mt-1">Adicionar novo serviço a um cliente existente</p>
            </button>
          </div>
        </div>
      )}

      {/* PASSO 2 — Selecione o serviço (grade) */}
      {step === 2 && (
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
          {/* Categorias padronizadas (PRD Nova Conexão §1): mesmo layout p/ todas */}
          <p className="font-heading font-semibold text-gray-900 mb-3">Categorias</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            <button onClick={() => nav('/registros/protecao-veicular/nova')}
              className="text-left rounded-2xl border border-gray-200 p-4 hover:border-accent-400 hover:shadow-md transition">
              <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow mb-2"><ShieldCheck size={18} className="text-white" /></span>
              <span className="block font-semibold text-gray-900 text-sm">Proteção Veicular</span>
              <span className="block text-[10px] font-bold text-accent-600 uppercase">Novo · Cotação rápida</span>
            </button>
            <button onClick={() => nav('/registros/telecom')}
              className="text-left rounded-2xl border border-gray-200 p-4 hover:border-accent-400 hover:shadow-md transition">
              <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-indigo-600 flex items-center justify-center shadow mb-2"><Smartphone size={18} className="text-white" /></span>
              <span className="block font-semibold text-gray-900 text-sm">Telecom</span>
              <span className="block text-[10px] font-bold text-accent-600 uppercase">Novo · Chips e planos</span>
            </button>
            <button onClick={abrirEnergia}
              className="text-left rounded-2xl border border-gray-200 p-4 hover:border-accent-400 hover:shadow-md transition">
              <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow mb-2"><Zap size={18} className="text-white" /></span>
              <span className="block font-semibold text-gray-900 text-sm">Energia por Assinatura</span>
              <span className="block text-[10px] font-bold text-accent-600 uppercase">Novo · Conexão GD</span>
            </button>
            <button onClick={abrirAlarme}
              className="text-left rounded-2xl border border-gray-200 p-4 hover:border-accent-400 hover:shadow-md transition">
              <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center shadow mb-2"><Bell size={18} className="text-white" /></span>
              <span className="block font-semibold text-gray-900 text-sm">Sistema de Alarme Smart</span>
              <span className="block text-[10px] font-bold text-accent-600 uppercase">{alarmeOferta ? 'Novo · Monitoramento 24h' : 'Novo · Em breve'}</span>
            </button>
          </div>

          <p className="font-heading font-semibold text-gray-900 mb-4">Outros serviços recorrentes</p>
          {recorrentesOutros.length === 0 && <p className="text-sm text-gray-400">Nenhum outro serviço recorrente disponível. (O administrador cadastra os serviços.)</p>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {recorrentesOutros.map((o) => (
              <button key={o.id} onClick={() => { setOfertaId(o.id); irParaForm() }}
                className={`flex items-center gap-3 text-left rounded-2xl border p-4 hover:shadow-md transition ${Number(ofertaId) === o.id ? 'border-accent-400 ring-1 ring-accent-300' : 'border-gray-200'}`}>
                <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow shrink-0"><Zap size={18} className="text-white" /></span>
                <span>
                  <span className="block font-semibold text-gray-900">{o.nome}</span>
                  <span className="block text-[11px] text-gray-400">{o.categoria ? o.categoria.toUpperCase() : 'SERVIÇO'} · {o.pv} PV</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* PASSO 3 — Formulário da conexão */}
      {step === 3 && (
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 space-y-4">
          {isAdmin && (
            <div>
              <label className="block text-sm font-medium mb-1">Licenciado responsável</label>
              <SearchableSelect value={licId} onChange={setLicId} placeholder="Buscar licenciado..."
                options={licenciados.map((l) => ({ value: l.id, label: l.nome }))} />
            </div>
          )}

          {ofertaSel?.categoria === 'energia' ? (
            <ConexaoGD ofertaSel={ofertaSel} isAdmin={isAdmin} licId={licId} meuLic={meuLic}
              onCriado={(d) => { setCriado(d); setStep(4) }} />
          ) : (
          <>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Zap size={16} className="text-accent-500" /> Serviço: <b className="text-gray-900">{ofertaSel?.nome}</b> · {ofertaSel?.pv} PV
          </div>

          {modo === 'existente' ? (
            <div>
              <label className="block text-sm font-medium mb-1">Cliente</label>
              <SearchableSelect value={clienteId} onChange={setClienteId} placeholder="Buscar cliente..."
                options={clientes.map((c) => ({ value: c.id, label: `${c.nome}${c.cpf_cnpj ? ' · ' + c.cpf_cnpj : ''}` }))} />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><label className="block text-sm font-medium mb-1">Nome do cliente</label><input className={inp} value={novoCli.nome} onChange={(e) => setNovoCli({ ...novoCli, nome: e.target.value })} /></div>
              <div><label className="block text-sm font-medium mb-1">CPF/CNPJ</label><input className={inp} value={novoCli.cpf_cnpj} onChange={(e) => setNovoCli({ ...novoCli, cpf_cnpj: e.target.value })} /></div>
              <div><label className="block text-sm font-medium mb-1">E-mail</label><input className={inp} value={novoCli.email} onChange={(e) => setNovoCli({ ...novoCli, email: e.target.value })} /></div>
              <div><label className="block text-sm font-medium mb-1">WhatsApp</label><input className={inp} value={novoCli.whatsapp} onChange={(e) => setNovoCli({ ...novoCli, whatsapp: e.target.value })} /></div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-1">Cliente que indicou <span className="text-gray-400 font-normal">(opcional — cashback na 1ª fatura)</span></label>
            <SearchableSelect value={indicadorId} onChange={setIndicadorId} placeholder="Nenhum / buscar..."
              options={clientes.map((c) => ({ value: c.id, label: c.nome }))} />
          </div>

          {ofertaSel?.campos_schema?.length > 0 && (
            <div className="border-t border-gray-100 pt-4">
              <p className="text-sm font-medium mb-3">Dados de <span className="text-accent-600">{ofertaSel.nome}</span></p>
              <CamposRenderer schema={ofertaSel.campos_schema} value={dados} onChange={setDados}
                files={arquivos} onFile={(chave, f) => setArquivos((a) => ({ ...a, [chave]: f }))} />
            </div>
          )}

          <button onClick={criar} className="rounded-lg bg-gradient-to-r from-primary-500 to-accent-500 text-white px-5 py-2 font-semibold shadow">Cadastrar conexão</button>
          {msg && <p className="text-sm text-tc-graphite bg-amber-50 rounded px-3 py-2">{msg}</p>}
          </>
          )}
        </div>
      )}

      {/* PASSO 4 — Enviado para análise */}
      {step === 4 && criado && (
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 space-y-3 text-center">
          <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto"><CheckCircle2 size={28} className="text-emerald-600" /></div>
          <p className="font-heading font-bold text-gray-900 text-lg">Cadastro enviado para análise</p>
          <p className="text-sm text-gray-500 max-w-md mx-auto">
            O cadastro <b>#{criado.id}</b> entrou na <b>Fila de Análise</b>. Nenhum ponto ou comissão é gerado
            até a aprovação do administrador. Você será notificado sobre o resultado da análise.
          </p>
          <div className="flex gap-3 justify-center pt-1">
            <button onClick={reset} className="rounded-lg bg-gradient-to-r from-primary-500 to-accent-500 text-white px-5 py-2 font-semibold shadow">+ Nova conexão</button>
          </div>
        </div>
      )}
    </div>
  )
}
